import React from 'react';
import {useHistory} from 'react-router-dom';
import {useIntl, FormattedMessage} from 'react-intl';
import Styled from 'styled-components/macro';
import {Modal} from '@forecast-it/design-system';
import {
	EconomicIcon,
	HubspotIcon,
	PipedriveIcon,
	QuickbooksIcon,
	SageIntacctIcon,
	SalesforceIcon,
	XeroIcon,
} from '@/web-components';
import DropdownMenu from '../shared/components/dropdown-menu/DropdownMenu';
import {clientLogoSrc} from '../../directApi';
import {getClientInitials} from './clientsAvatarUtil';
import DeleteClientMutation from '../../mutations/delete_client_mutation';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import {createToast} from '../shared/components/toasts/another-toast/toaster';
import {ConfirmationModalPrimaryButton} from '../shared/components/modals/general/ConfirmationModal';
import Util from '../shared/util/util';
import {SOCKET_ACTION} from '../../constants';
import moreHorizontalIcon from '../../images/components/overlay/navigation-show-more-horizontal-black.svg';

export type ClientRowData = {
	id: string;
	name: string;
	notes: string | null;
	projectCount: number;
	logoId: string | null;
	companyId: string;
	// True when the client has one or more invoices — delete is then disabled (IGNITE-569)
	hasInvoices: boolean;
	// True when the client has one or more contacts of any status — delete is then disabled (IGNITE-597)
	hasContacts: boolean;
	// Client-level integration ids — each drives a per-row icon + an "Open in" kebab link.
	// Each id is already gated on its company integration being enabled in ClientsPage.tsx
	// (quickbooks is intentionally ungated, mirroring the old clients page).
	hubspotCompanyId: string | null;
	xeroContactId: string | null;
	economicCustomerId: string | null;
	quickbooksCustomerId: string | null;
	salesforceAccountId: string | null;
	sageIntacctCustomerId: string | null;
	pipedriveOrganizationId: string | null;
	// Company-level integration fields (same for all rows) — needed to build the "Open in" URLs
	hubspotAccountId: string | null;
	pipedriveCompanyDomain: string | null;
	salesforceUrl: string | null;
};

const AVATAR_SIZE = 32;

const AvatarImg = Styled.img`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
`;

// Color matches ContactAvatar on /clients/:id (CompanyViewHeader.tsx)
const AvatarInitials = Styled.div`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: 4px;
  background-color: #0004db;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.theme.comp.avatar.small.text};
  flex-shrink: 0;
  user-select: none;
`;

// Inner div wrappers are required because <td> does not reliably support display:flex
const NameCellInner = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BorderlessMoreButton = Styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

// Carries the row's type so the cell text (and the name span inside it) uses the
// themed family instead of inheriting the global stylesheet's body font.
const Td = Styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #e5e5e5;
  ${props => props.theme.sys.font.body['1']};
`;

const NotesTd = Styled(Td)`
  max-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const ProjectsTd = Styled(Td)`
  text-align: right;
  white-space: nowrap;
`;

// Integration icons live in their own headerless column to the right of the project count. With
// table-layout: auto this column expands to fit however many icons the busiest row has; rows with
// fewer (or no) integrations simply leave it blank.
const IconsTd = Styled(Td)`
  white-space: nowrap;
`;

const IconsInner = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  & > * {
    flex-shrink: 0;
  }
`;

const ActionsTd = Styled(Td)`
  padding: 4px 8px;
`;

const ActionsInner = Styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

type Props = {
	client: ClientRowData;
};

function extractNotesText(notes: string | null): string {
	if (!notes) return '';
	try {
		const parsed = JSON.parse(notes);
		if (parsed && Array.isArray(parsed.blocks) && 'entityMap' in parsed) {
			return parsed.blocks
				.map((b: {text: string}) => b.text)
				.join('\n')
				.trim();
		}
	} catch {
		// not JSON — treat as plain text
	}
	return notes;
}

const ClientsListItem: React.FC<Props> = ({client}) => {
	const {formatMessage} = useIntl();
	const history = useHistory();

	const handleDelete = () => {
		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'clients_page.delete_client_modal_title'}),
			description: (
				<div style={{whiteSpace: 'pre-line'}}>
					<FormattedMessage
						id="clients_page.delete_client_modal_description"
						values={{
							clientName: client.name,
							confirmText: SOCKET_ACTION.DELETE,
							b: (chunks: React.ReactNode) => <b>{chunks}</b>,
						}}
					/>
				</div>
			),
			modalSize: Modal.SIZE.MEDIUM,
			confirmPhrase: SOCKET_ACTION.DELETE,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.DELETE,
			callbackPositive: () => {
				Util.CommitMutation(DeleteClientMutation, {id: client.id, companyId: client.companyId}, () => {
					createToast({
						duration: 5000,
						message: formatMessage({id: 'client.has-been-deleted'}),
					});
				});
			},
		});
	};

	const notesText = extractNotesText(client.notes);

	const deleteDisabled = client.hasInvoices || client.hasContacts;
	// Invoiced reason wins when both apply (keeps IGNITE-569 behaviour intact).
	const deleteTooltip = client.hasInvoices
		? formatMessage({id: 'settings.client_delete_invoiced'})
		: client.hasContacts
		? formatMessage({id: 'clients.client_delete_has_contacts'})
		: undefined;

	const avatar = client.logoId ? (
		<AvatarImg crossOrigin="use-credentials" src={clientLogoSrc(client.logoId)} alt="company logo" />
	) : (
		<AvatarInitials aria-hidden="true">{getClientInitials(client.name)}</AvatarInitials>
	);

	return (
		<tr
			data-cy="clients-list-item"
			style={{cursor: 'pointer'}}
			tabIndex={0}
			onClick={() => history.push(`/clients/${client.id}`)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					history.push(`/clients/${client.id}`);
				}
			}}
		>
			<Td>
				<NameCellInner>
					{avatar}
					<span>{client.name}</span>
				</NameCellInner>
			</Td>
			<NotesTd title={notesText || undefined}>{notesText || '-'}</NotesTd>
			<ProjectsTd>{client.projectCount}</ProjectsTd>
			<IconsTd>
				<IconsInner>
					{client.hubspotCompanyId && (
						<span data-testid="hubspot-indicator" title={formatMessage({id: 'clients_page.hubspot_linked'})}>
							<HubspotIcon size={HubspotIcon.SIZE.STANDARD} />
						</span>
					)}
					{client.xeroContactId && (
						<span data-testid="xero-indicator" title={formatMessage({id: 'clients_page.xero_linked'})}>
							<XeroIcon size={XeroIcon.SIZE.STANDARD} />
						</span>
					)}
					{client.economicCustomerId && (
						<span data-testid="economic-indicator" title={formatMessage({id: 'clients_page.economic_linked'})}>
							<EconomicIcon size={EconomicIcon.SIZE.STANDARD} />
						</span>
					)}
					{client.quickbooksCustomerId && (
						<span data-testid="quickbooks-indicator" title={formatMessage({id: 'clients_page.quickbooks_linked'})}>
							<QuickbooksIcon size={QuickbooksIcon.SIZE.STANDARD} />
						</span>
					)}
					{client.salesforceAccountId && (
						<span data-testid="salesforce-indicator" title={formatMessage({id: 'clients_page.salesforce_linked'})}>
							<SalesforceIcon size={SalesforceIcon.SIZE.STANDARD} />
						</span>
					)}
					{client.sageIntacctCustomerId && (
						<span
							data-testid="sage-intacct-indicator"
							title={formatMessage({id: 'clients_page.sage_intacct_linked'})}
						>
							<SageIntacctIcon size={SageIntacctIcon.SIZE.STANDARD} />
						</span>
					)}
					{client.pipedriveOrganizationId && (
						<span data-testid="pipedrive-indicator" title={formatMessage({id: 'clients_page.pipedrive_linked'})}>
							<PipedriveIcon size={PipedriveIcon.SIZE.STANDARD} />
						</span>
					)}
				</IconsInner>
			</IconsTd>
			<ActionsTd onClick={e => e.stopPropagation()}>
				<ActionsInner>
					<DropdownMenu
						trigger={
							<BorderlessMoreButton aria-label="More actions">
								<img src={moreHorizontalIcon} alt="" width={20} height={20} />
							</BorderlessMoreButton>
						}
					>
						<DropdownMenu.Item
							onClick={() => showModal({type: MODAL_TYPE.EDIT_CLIENT_DETAILS, clientId: client.id})}
						>
							{formatMessage({id: 'common.edit'})}
						</DropdownMenu.Item>
						<DropdownMenu.Item
							onClick={handleDelete}
							disabled={deleteDisabled}
							tooltip={deleteTooltip}
							dataCy="delete-client"
						>
							{formatMessage({id: 'common.delete'})}
						</DropdownMenu.Item>
						{client.hubspotCompanyId && client.hubspotAccountId && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(
										`https://app.hubspot.com/contacts/${client.hubspotAccountId}/company/${client.hubspotCompanyId}/`,
										'_blank'
									)
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'HubSpot'})}
							</DropdownMenu.Item>
						)}
						{client.xeroContactId && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(`https://go.xero.com/Contacts/View/${client.xeroContactId}`, '_blank')
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'Xero'})}
							</DropdownMenu.Item>
						)}
						{client.economicCustomerId && (
							<DropdownMenu.Item
								onClick={() => window.open('https://secure.e-conomic.com/sales/customers', '_blank')}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'E-conomic'})}
							</DropdownMenu.Item>
						)}
						{client.quickbooksCustomerId && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(
										`${process.env.QUICKBOOKS_BASE_URL}/app/customerdetail?nameId=${client.quickbooksCustomerId}`,
										'_blank'
									)
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'QuickBooks'})}
							</DropdownMenu.Item>
						)}
						{client.salesforceAccountId && client.salesforceUrl && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(
										`${client.salesforceUrl}/lightning/r/Account/${client.salesforceAccountId}/view`,
										'_blank'
									)
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'Salesforce'})}
							</DropdownMenu.Item>
						)}
						{client.pipedriveOrganizationId && client.pipedriveCompanyDomain && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(
										`${client.pipedriveCompanyDomain}/organization/${client.pipedriveOrganizationId}`,
										'_blank'
									)
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'Pipedrive'})}
							</DropdownMenu.Item>
						)}
					</DropdownMenu>
				</ActionsInner>
			</ActionsTd>
		</tr>
	);
};

// Memoized because the clients list renders every client in the company as a row, and searching
// re-renders the list on each keystroke. The row objects are rebuilt only when the underlying
// query data changes, not when the search term does, so a shallow prop compare skips the rows
// whose data did not move.
export default React.memo(ClientsListItem);
